import Icon from "../../../components/icons";

export function Card({
  title,
  children,
  action,
}: {
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div style={cardStyle}>
      {title && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span
            className="fw-semibold"
            style={{ fontSize: 12, color: "#777", letterSpacing: "0.04em" }}
          >
            {title.toUpperCase()}
          </span>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function InfoRow({
  icon,
  children,
  href,
  iconNode,
}: {
  icon?: string;
  children?: React.ReactNode;
  href?: string;
  iconNode?: React.ReactNode;
}) {
  if (!children) return null;
  const content = href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-body text-decoration-none"
    >
      {children}
    </a>
  ) : (
    children
  );
  return (
    <div className="d-flex align-items-center gap-3 py-2">
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{
          width: 34,
          height: 34,
          backgroundColor: "rgba(240,129,63,0.12)",
        }}
      >
        {iconNode ?? <Icon name={icon!} width={16} height={16} />}
      </span>
      <span style={{ fontSize: 14, minWidth: 0, wordBreak: "break-word" }}>
        {content}
      </span>
    </div>
  );
}

export function Stars({ value }: { value: number }) {
  return (
    <span
      className="d-inline-flex align-items-center gap-1 fw-bold"
      style={{ color: ACCENT }}
    >
      <Icon name="star" width={18} height={18} />
      {value.toFixed(1)}
      <span className="text-muted fw-normal" style={{ fontSize: 12 }}>
        /5
      </span>
    </span>
  );
}

export const ACCENT = "#f0813f";

const TABS = [
  { key: "about", label: "À propos" },
  { key: "services", label: "Services" },
  { key: "offers", label: "Offres" },
  { key: "events", label: "Événements" },
  { key: "packs", label: "Packs" },
  { key: "reviews", label: "Avis" },
] as const;

export type TabKey = (typeof TABS)[number]["key"];

export const cardStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  borderRadius: 16,
  padding: "18px 20px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

export function StatTile({
  label,
  value,
  onClick,
}: {
  label: string;
  value: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-0 text-start flex-fill"
      style={{ ...cardStyle, padding: "14px 18px", minWidth: 120 }}
    >
      <div
        className="fw-bold"
        style={{ fontSize: 24, color: ACCENT, lineHeight: 1.1 }}
      >
        {value}
      </div>
      <div className="text-muted" style={{ fontSize: 12 }}>
        {label}
      </div>
    </button>
  );
}

export function EmptyState({ text }: { text: string }) {
  return (
    <div
      className="text-center text-muted py-5"
      style={{ border: "1px dashed #ddd", borderRadius: 12, fontSize: 14 }}
    >
      {text}
    </div>
  );
}

export function PillTabs({
  active,
  onChange,
  counts,
  hidden = [],
}: {
  active: TabKey;
  onChange: (k: TabKey) => void;
  counts: Partial<Record<TabKey, number>>;
  hidden?: TabKey[];
}) {
  return (
    <div
      className="d-flex gap-2 mb-3 pb-1"
      style={{ overflowX: "auto", whiteSpace: "nowrap" }}
    >
      {TABS.filter((t) => !hidden.includes(t.key)).map((tab) => {
        const isActive = tab.key === active;
        const count = counts[tab.key];
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="border-0 rounded-pill flex-shrink-0"
            style={{
              padding: "8px 18px",
              fontSize: 14,
              fontWeight: 600,
              color: isActive ? "#fff" : "#444",
              backgroundColor: isActive ? ACCENT : "#fff",
              boxShadow: isActive
                ? "0 4px 12px rgba(240,129,63,0.35)"
                : "0 1px 3px rgba(0,0,0,0.08)",
              transition: "all .15s ease",
            }}
          >
            {tab.label}
            {count !== undefined && (
              <span style={{ opacity: 0.75 }}> · {count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
